import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AuthenticateRequestDto, AuthenticationResponseDto, ChangePasswordRequestDto, ChangePasswordResponseDto, ForgotPasswordRequestDto, MessageResponseDto, ResetPasswordRequestDto, RegisterCandidateRequestDto, SendOtpRequestDto, SendOtpResponseDto, VerifyOtpRequestDto, VerifyEmailResponseDto, MagicLinkLoginResponseDto } from "../models/auth.model";
import { environment } from "../../../../environments/environment";


/**
 * Auth API — pokriva /auth/* endpointe (login, social login, signup, login OTP,
 * refresh token). Candidate-portal pozivi (profil, prijave, CV, phone verify)
 * žive u CandidatePortalApiService.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService {
    private http: HttpClient = inject(HttpClient);
    private apiUrl = environment.apiBaseUrl;

    /**
     * POST /auth/authenticate — email login.
     * withCredentials:true → preglednik sprema HttpOnly refresh cookie
     * (koristi ga budući refresh-token task).
     */
    authenticate(body: AuthenticateRequestDto): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * POST /auth/authenticate/google — Google login.
     * idToken se šalje kao `Authorization: Bearer` header (NE u body) —
     * tako backend implementira /authenticate/google
     * (AuthenticationController#googleAuthenticate). Body ostaje prazan.
     */
    authenticateGoogle(idToken: string): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate/google`,
            {},
            {
                headers: { Authorization: `Bearer ${idToken}` },
                withCredentials: true,
            },
        );
    }

    /**
     * POST /auth/authenticate/facebook — Facebook login.
     * accessToken iz FB SDK-a se šalje kao `Authorization: Bearer` header
     * (NE u body) — backend ga čita iz Authorization headera i radi
     * substring(7) (AuthenticationController#facebookAuthenticate), identično
     * kao /authenticate/google. Body ostaje prazan.
     * withCredentials:true → preglednik sprema HttpOnly refresh cookie.
     */
    authenticateFacebook(accessToken: string): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate/facebook`,
            {},
            {
                headers: { Authorization: `Bearer ${accessToken}` },
                withCredentials: true,
            },
        );
    }

    /**
     * POST /auth/register-candidate — candidate signup.
     * Backend radi auto-login → vraća isti AuthenticationResponse kao authenticate.
     */
    registerCandidate(body: RegisterCandidateRequestDto): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/register-candidate`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * POST /auth/otp/request — pošalji SMS s OTP kodom.
     * Telefon se normalizira na backend-u (strip non-digits).
     * Ne setira cookie, ali withCredentials:true zbog konzistencije s ostalim auth pozivima.
     */
    sendOtp(body: SendOtpRequestDto): Observable<SendOtpResponseDto> {
        return this.http.post<SendOtpResponseDto>(
            `${this.apiUrl}/auth/otp/request`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * POST /auth/otp/verify — provjeri OTP kod i izvrši login.
     * Vraća isti AuthenticationResponseDto kao /authenticate i setira
     * HttpOnly refresh cookie (zato withCredentials:true).
     */
    verifyOtp(body: VerifyOtpRequestDto): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/otp/verify`,
            body,
            { withCredentials: true },
        );
    }

    refreshToken(): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/refresh-token`,
            {},
            { withCredentials: true },
        );
    }

    /**
     * Postavljanje/promjena lozinke. Traži prijavljenog korisnika (backend čita
     * `findCurrentLoggedInUser`), pa ide s Authorization headerom kao i ostalo.
     *
     * `currentPassword` se izostavlja kod prve prijave — backend tada preskače
     * provjeru stare lozinke (`ResetPasswordService`: uvjet je `!isFirstLogin`).
     */
    changePassword(body: ChangePasswordRequestDto): Observable<ChangePasswordResponseDto> {
        return this.http.post<ChangePasswordResponseDto>(
            `${this.apiUrl}/auth/change-password`,
            body,
        );
    }

    /**
     * Zatraži link za promjenu lozinke. Backend NAMJERNO uvijek vrati 200 s istom
     * porukom, i za nepostojeći e-mail — da se preko forme ne može doznati tko ima
     * račun. Zato ni frontend ne smije razlikovati ta dva slučaja.
     */
    forgotPassword(body: ForgotPasswordRequestDto): Observable<MessageResponseDto> {
        return this.http.post<MessageResponseDto>(`${this.apiUrl}/auth/forgot-password`, body);
    }

    /**
     * Postavi novu lozinku pomoću tokena iz e-maila. Neispravan ili istekao token
     * vraća 400 (`BadRequestException` iz ForgotPasswordService).
     */
    resetPassword(body: ResetPasswordRequestDto): Observable<MessageResponseDto> {
        return this.http.post<MessageResponseDto>(`${this.apiUrl}/auth/reset-password`, body);
    }

    /**
     * Potvrdi e-mail tokenom iz linka. Sve greske su 400 (`BadRequestException`),
     * a razlikuju se samo tekstom poruke — vidi `verify-email-page.component.ts`.
     */
    verifyEmail(token: string): Observable<VerifyEmailResponseDto> {
        return this.http.get<VerifyEmailResponseDto>(`${this.apiUrl}/auth/verify-email`, {
            params: { token },
        });
    }

    /**
     * POST /public/magic-link/login — prijava jednokratnim tokenom iz e-maila.
     * Javni endpoint (bez Bearera). withCredentials:true je OBAVEZAN: backend uz
     * odgovor salje HttpOnly refresh cookie, bez kojeg sesija umire s JWT-om.
     */
    magicLinkLogin(token: string): Observable<MagicLinkLoginResponseDto> {
        return this.http.post<MagicLinkLoginResponseDto>(
            `${this.apiUrl}/public/magic-link/login`,
            { token },
            { withCredentials: true },
        );
    }

    /**
     * Posalji novi verifikacijski link. Backend uzima korisnika iz JWT-a
     * (`findCurrentLoggedInUser`), pa radi SAMO za prijavljenog — stranica gumb
     * i nudi samo tada.
     */
    resendVerification(): Observable<MessageResponseDto> {
        return this.http.post<MessageResponseDto>(`${this.apiUrl}/auth/resend-verification`, {});
    }
}
